import type { Node as PMNode } from '@tiptap/pm/model'
import { Plugin, PluginKey, type Transaction } from '@tiptap/pm/state'
import type { EditorView } from '@tiptap/pm/view'
import { columnResizingPluginKey } from '@tiptap/pm/tables'
import { TableView, updateColumns } from '@tiptap/extension-table'
import { normaliseWidths } from '../../lib/table-geometry'
import { MarkdownTable, tablePercents } from './table-markdown'

/**
 * The Rich editor's table: `MarkdownTable` (widths stored in the delimiter row) plus the two screen
 * concerns the server never needs — drawing a sized table, and turning a border drag into widths.
 *
 * THE DOC HOLDS PERCENTAGES, NEVER PIXELS. The table node's `colPercents` is the only width that is
 * kept: it is what the serializer writes, so a pixel number never reaches the Y.Text. prosemirror-
 * tables' column resizing, however, speaks pixels: while a border is dragged it only restyles the
 * DOM, and on mouseup it commits ONE transaction setting the dragged column's cells' `colwidth`, then
 * dispatches `setDragging: null`. The commit plugin below rides on that second dispatch and, in the
 * same `appendTransaction`, converts the pixels into percentages and clears every `colwidth`. One
 * drag therefore changes the markdown exactly once — one Y.Text write, one history entry — and
 * nothing at all while the mouse moves.
 *
 * DRAWING. A sized table spans the full text column and each column is a percentage of it. The
 * widths go on the `<col>` elements as the `width` ATTRIBUTE, deliberately not as a style:
 * prosemirror-tables restyles `col.style.width` on every mouse move, so the dragged column takes
 * its pixel width while the others keep their proportions of what is left — which is exactly what
 * the commit then stores.
 *
 * Locks and read-only need nothing here. The mouseup commit is an ordinary doc transaction, so
 * SectionNumbers' filter refuses it inside a § someone else holds (with the usual toast) and no
 * `colwidth` is ever set; and prosemirror-tables offers no handle at all on a non-editable view.
 */

/** The width a column is converted against when there is no layout to measure (jsdom). */
const FALLBACK_TABLE_WIDTH_PX = 1000

/** Live views by their wrapper element, so the drag-end refresh can reach them. */
const views = new WeakMap<Element, PercentTableView>()

/** tiptap's TableView, drawing `colPercents` when the table has them. */
export class PercentTableView extends TableView {
  constructor(node: PMNode, cellMinWidth: number, view?: EditorView, HTMLAttributes: Record<string, unknown> = {}) {
    super(node, cellMinWidth, view, HTMLAttributes)
    views.set(this.dom, this)
    this.drawPercents()
  }

  update(node: PMNode): boolean {
    if (!super.update(node)) return false
    this.drawPercents()
    return true
  }

  /** Redraw from the node — after a drag the DOM may hold widths the doc never accepted. */
  refresh(): void {
    updateColumns(this.node, this.colgroup, this.table, this.cellMinWidth)
    this.drawPercents()
  }

  private drawPercents(): void {
    // A column carrying pixels is mid-commit (or pasted HTML): tiptap's own pixel drawing stands.
    const percents = hasPixelWidths(this.node) ? null : tablePercents(this.node)
    const cols = Array.from(this.colgroup.children) as HTMLTableColElement[]
    cols.forEach((col, i) => {
      if (!percents) {
        col.removeAttribute('width')
        return
      }
      col.style.removeProperty('width')
      col.style.removeProperty('min-width')
      col.setAttribute('width', `${percents[i]}%`)
    })
    if (percents) {
      this.table.style.width = ''
      this.table.style.minWidth = ''
    }
  }
}

/** The first `colwidth` pixel value of each column (null for a column that has none), or null
 *  for a table with merged cells. */
function pixelWidths(table: PMNode): (number | null)[] | null {
  const head = table.firstChild
  if (!head) return null
  const columns = head.childCount
  const px: (number | null)[] = new Array<number | null>(columns).fill(null)
  let spans = false
  table.forEach((row) =>
    row.forEach((cell, _o, j) => {
      if ((cell.attrs.colspan as number) > 1 || (cell.attrs.rowspan as number) > 1) spans = true
      const w = (cell.attrs.colwidth as number[] | null)?.[0]
      if (j < columns && w && px[j] == null) px[j] = w
    }),
  )
  return spans ? null : px
}

function hasPixelWidths(table: PMNode): boolean {
  let found = false
  table.descendants((n) => {
    if ((n.attrs.colwidth as number[] | null)?.some((w) => w > 0)) found = true
    return !found
  })
  return found
}

/**
 * The new width set after columns were given pixel widths. Each sized column becomes its share of
 * the table's width; the others keep their previous proportions of whatever is left. A change too
 * small to move any sized column by a whole percent keeps the previous set, so clicking a border
 * (which prosemirror-tables commits as a "resize" to the current width) never rewrites anything.
 */
export function commitPixelWidths(
  previous: number[] | null,
  px: (number | null)[],
  tableWidthPx: number,
): number[] | null {
  const columns = px.length
  const base = previous ?? new Array<number>(columns).fill(100 / columns)
  const sized = px.map((w) => (w ? (w / tableWidthPx) * 100 : null))
  if (sized.every((s, i) => s === null || Math.abs(s - base[i]!) < 1)) return previous
  const rest = Math.max(0, 100 - sized.reduce<number>((a, s) => a + (s ?? 0), 0))
  const baseRest = base.reduce((a, b, i) => (sized[i] === null ? a + b : a), 0)
  const raw = sized.map((s, i) => s ?? (baseRest > 0 ? (rest * base[i]!) / baseRest : 0))
  return normaliseWidths(raw)
}

/** Convert pixel widths into `colPercents`, and drop `colPercents` that no longer fit the columns. */
function commitWidths(tr: Transaction, view: EditorView | null, doc: PMNode): void {
  doc.descendants((node, pos) => {
    // Tables sit in blocks (a list item, a quote); nothing inside a paragraph can hold one.
    if (node.type.name !== 'table') return !node.isTextblock
    const px = pixelWidths(node)
    // A merged-cell table is not GFM — it serialises as `[table]`, so its widths stay local.
    if (!px) return false
    const stored = node.attrs.colPercents as number[] | null
    let next = stored && stored.length === px.length ? stored : null
    if (px.some((w) => w !== null)) {
      const measured = (view?.nodeDOM(pos) as HTMLElement | null)?.clientWidth ?? 0
      next = commitPixelWidths(tablePercents(node), px, measured > 0 ? measured : FALLBACK_TABLE_WIDTH_PX)
    }
    if (next !== stored) tr.setNodeMarkup(pos, undefined, { ...node.attrs, colPercents: next })
    // Pixels never outlive the drag that made them: the percentages above are the only width kept.
    node.descendants((cell, cellPos) => {
      if (cell.attrs.colwidth != null) {
        tr.setNodeMarkup(pos + 1 + cellPos, undefined, { ...cell.attrs, colwidth: null })
      }
      return cell.type.name === 'tableRow'
    })
    return false
  })
}

const commitKey = new PluginKey('tableWidthCommit')

function commitPlugin(): Plugin {
  let editorView: EditorView | null = null
  return new Plugin({
    key: commitKey,
    view(view) {
      editorView = view
      return {
        update(view, prev) {
          // A drag that ends without an accepted commit (refused by a § lock) leaves the DOM
          // showing a width the doc never took: redraw every table from its node.
          const was = columnResizingPluginKey.getState(prev)?.dragging
          const now = columnResizingPluginKey.getState(view.state)?.dragging
          if (was && !now) view.dom.querySelectorAll('.tableWrapper').forEach((el) => views.get(el)?.refresh())
        },
        destroy() {
          editorView = null
        },
      }
    },
    appendTransaction(transactions, _old, state) {
      const dragEnded = (tr: Transaction): boolean =>
        (tr.getMeta(columnResizingPluginKey) as { setDragging?: unknown } | undefined)?.setDragging === null
      if (!transactions.some((tr) => tr.docChanged || dragEnded(tr))) return null
      // Mid-drag: the mouseup commit lands before `setDragging: null`; convert on the latter.
      if (columnResizingPluginKey.getState(state)?.dragging) return null
      const tr = state.tr
      commitWidths(tr, editorView, state.doc)
      return tr.docChanged ? tr : null
    },
  })
}

export const ResizableTable = MarkdownTable.extend({
  addOptions() {
    return { ...this.parent!(), View: PercentTableView }
  },

  addProseMirrorPlugins() {
    return [...(this.parent?.() ?? []), commitPlugin()]
  },
})
